import React, { useEffect, useState, useRef, useCallback } from 'react';
import { apiFetch } from '../../utils/api';
import { exportToExcel } from '../../utils/exportExcel';
import { useAuth } from '../../context/AuthContext';
import useIsMobile from '../../utils/useIsMobile';
import { useFeatures } from '../../utils/useFeatures';

const EMPTY = { name: '', address: '', city: '', state: '', spoc_name: '', contact_number: '', customer_email: '', email_domain: '', product_category: '', product: '', serial_number: '', amc_start_date: '', amc_end_date: '', po_number: '', po_date: '', notes: '' };

const INDIA_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh',
  'Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan',
  'Sikkim','Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
  'Andaman & Nicobar Islands','Chandigarh','Dadra & Nagar Haveli and Daman & Diu',
  'Delhi','Jammu & Kashmir','Ladakh','Lakshadweep','Puducherry',
];
const EMPTY_LOC = { location: '', spoc_name: '', spoc_designation: '', spoc_department: '', spoc_phone: '', spoc_email: '' };

function amcStatus(end) {
  if (!end) return null;
  const days = Math.ceil((new Date(end) - new Date()) / 86400000);
  if (days < 0) return { label: 'Expired', color: '#b91c1c', bg: 'var(--red-50)' };
  if (days <= 30) return { label: `Expires in ${days}d`, color: '#92400e', bg: 'var(--amber-100)' };
  return { label: 'Active', color: 'var(--status-resolved-text)', bg: 'var(--status-resolved-bg)' };
}

export default function Customers() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isMobile = useIsMobile();
  const [data, setData] = useState({ customers: [], total: 0 });
  const { tenant } = useFeatures();
  const visibleCustomers = (tenant?.enabled && tenant?.customer_id)
    ? data.customers.filter(c => c.id === tenant.customer_id)
    : data.customers;
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const searchRef = useRef(null);
  const PAGE_SIZE = 5;

  // Excel import / export state
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = React.useRef(null);

  // Main customer modal
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Locations (inside customer modal)
  const [locations, setLocations] = useState([]);
  const [locForm, setLocForm] = useState(EMPTY_LOC);
  const [editingLoc, setEditingLoc] = useState(null); // null | location object
  const [showLocForm, setShowLocForm] = useState(false);
  const [locSaving, setLocSaving] = useState(false);
  const [locError, setLocError] = useState('');
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  const [domains, setDomains] = useState([]);
  const [newDomain, setNewDomain] = useState('');
  const [domainErr, setDomainErr] = useState('');

  // Products (per-product AMC) inside customer modal
  const [prodRows, setProdRows] = useState([]);
  const [prodForm, setProdForm] = useState({ product_name: '', status: 'active', amc_start_date: '', amc_end_date: '', po_number: '', serial_number: '' });
  const [prodSaving, setProdSaving] = useState(false);

  const [categories, setCategories] = useState([]);
  const [allProducts, setAllProducts] = useState([]);

  // Modal tab
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'locations' | 'sla' | 'users'

  // Customer users tab
  const [custUsers, setCustUsers] = useState([]);
  const [userForm, setUserForm] = useState({ name: '', email: '', role: 'customer' });
  const [showUserForm, setShowUserForm] = useState(false);
  const [userSaving, setUserSaving] = useState(false);
  const [userError, setUserError] = useState('');

  // SLA overrides
  const [slaRules, setSlaRules] = useState([]);
  const [slaForm, setSlaForm] = useState({ product_id: '', priority: 'high', response_hours: '', resolution_hours: '' });
  const [slaSaving, setSlaSaving] = useState(false);
  const [slaError, setSlaError] = useState('');
  const PRIORITIES_SLA = ['critical', 'high', 'medium', 'low'];
  const PRIORITY_DEFAULTS = { critical: [1, 4], high: [4, 8], medium: [8, 24], low: [24, 72] };

  const filteredProducts = form.product_category
    ? allProducts.filter(p => p.category_name === form.product_category)
    : [];

  useEffect(() => {
    apiFetch('/products/categories').then(setCategories).catch(() => {});
    apiFetch('/products').then(setAllProducts).catch(() => {});
  }, []);

  // Debounce search 350ms
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  async function load(p = page, s = debouncedSearch) {
    setLoading(true);
    setSelectedIdx(-1);
    const offset = (p - 1) * PAGE_SIZE;
    const q = new URLSearchParams({ limit: PAGE_SIZE, offset });
    if (s) q.set('search', s);
    const d = await apiFetch(`/customers?${q}`);
    setData(d);
    setLoading(false);
  }

  useEffect(() => { load(1, debouncedSearch); }, [debouncedSearch]);
  useEffect(() => { load(page, debouncedSearch); }, [page]);

  // Keyboard navigation
  const handleKeyNav = useCallback((e) => {
    const customers = data.customers;
    if (!customers.length) return;
    const tag = document.activeElement?.tagName;
    if (tag === 'INPUT' && document.activeElement !== searchRef.current) return;
    if (tag === 'TEXTAREA') return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx(i => Math.min(i + 1, customers.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx(i => Math.max(i - 1, 0));
    }
  }, [data.customers]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyNav);
    return () => window.removeEventListener('keydown', handleKeyNav);
  }, [handleKeyNav]);

  function openAdd() {
    setForm(EMPTY);
    setLocations([]);
    setShowLocForm(false);
    setLocForm(EMPTY_LOC);
    setEditingLoc(null);
    setCurrentCustomerId(null);
    setError('');
    setModal('add');
  }

  async function openEdit(c) {
    const detail = await apiFetch(`/customers/${c.id}`);
    const matchedProduct = allProducts.find(p => p.name === c.product);
    setForm({
      name: detail.name,
      address: detail.address || '',
      city: detail.city || '',
      state: detail.state || '',
      spoc_name: detail.spoc_name || '',
      contact_number: detail.contact_number || '',
      customer_email: detail.customer_email || '',
      email_domain: detail.email_domain || '',
      product_category: matchedProduct?.category_name || '',
      product: detail.product || '',
      serial_number: detail.serial_number || '',
      amc_start_date: detail.amc_start_date || '',
      amc_end_date: detail.amc_end_date || '',
      po_number: detail.po_number || '',
      po_date: detail.po_date || '',
      notes: detail.notes || '',
    });
    setLocations(detail.locations || []);
    setProdRows(detail.products || []);
    setProdForm({ product_name: '', status: 'active', amc_start_date: '', amc_end_date: '', po_number: '', serial_number: '' });
    setShowLocForm(false);
    setLocForm(EMPTY_LOC);
    setEditingLoc(null);
    setCurrentCustomerId(c.id);
    setDomains([]); setNewDomain(''); setDomainErr('');
    apiFetch(`/customers/${c.id}/domains`).then(setDomains).catch(() => setDomains([]));
    setError('');
    setActiveTab('details');
    setSlaRules([]);
    setSlaError('');
    setSlaForm({ product_id: '', priority: 'high', response_hours: '', resolution_hours: '' });
    // Load SLA rules and users
    apiFetch(`/customers/${c.id}/sla`).then(setSlaRules).catch(() => {});
    apiFetch(`/customers/${c.id}/users`).then(setCustUsers).catch(() => {});
    setShowUserForm(false);
    setUserForm({ name: '', email: '', role: 'customer' });
    setUserError('');
    setModal(detail);
  }

  // ── Per-product AMC handlers ─────────────────────────────────────────────────
  async function addProduct() {
    if (!prodForm.product_name.trim() || !currentCustomerId) return;
    setProdSaving(true);
    try {
      const row = await apiFetch(`/customers/${currentCustomerId}/products`, { method: 'POST', body: prodForm });
      setProdRows(rs => [...rs, row]);
      setProdForm({ product_name: '', status: 'active', amc_start_date: '', amc_end_date: '', po_number: '', serial_number: '' });
    } catch (e) { setError(e.message); }
    setProdSaving(false);
  }
  // Update one field of a product row (optimistic + persist)
  function updateProductField(pid, field, value) {
    setProdRows(rs => rs.map(r => r.id === pid ? { ...r, [field]: value } : r));
  }
  async function saveProductField(pid, field, value) {
    try { await apiFetch(`/customers/${currentCustomerId}/products/${pid}`, { method: 'PATCH', body: { [field]: value } }); }
    catch (e) { setError(e.message); }
  }
  async function removeProduct(pid) {
    try {
      await apiFetch(`/customers/${currentCustomerId}/products/${pid}`, { method: 'DELETE' });
      setProdRows(rs => rs.filter(r => r.id !== pid));
    } catch (e) { setError(e.message); }
  }

  async function saveSlaRule(e) {
    e.preventDefault();
    setSlaSaving(true); setSlaError('');
    try {
      const body = {
        product_id: slaForm.product_id || null,
        priority: slaForm.priority,
        response_hours: parseFloat(slaForm.response_hours),
        resolution_hours: parseFloat(slaForm.resolution_hours),
      };
      const updated = await apiFetch(`/customers/${currentCustomerId}/sla`, { method: 'PUT', body });
      setSlaRules(updated);
      setSlaForm({ product_id: '', priority: 'high', response_hours: '', resolution_hours: '' });
    } catch (err) {
      setSlaError(err.message);
    } finally {
      setSlaSaving(false);
    }
  }

  async function saveUser(e) {
    e.preventDefault();
    if (!currentCustomerId) return;
    setUserSaving(true); setUserError('');
    try {
      const added = await apiFetch(`/customers/${currentCustomerId}/users`, { method: 'POST', body: userForm });
      setCustUsers(u => [...u, added]);
      setUserForm({ name: '', email: '', role: 'customer' });
      setShowUserForm(false);
    } catch (err) {
      setUserError(err.message);
    } finally {
      setUserSaving(false);
    }
  }

  async function deleteUser(userId) {
    if (!window.confirm('Remove this user from the customer?')) return;
    await apiFetch(`/customers/${currentCustomerId}/users/${userId}`, { method: 'DELETE' });
    setCustUsers(u => u.filter(x => x.id !== userId));
  }

  async function deleteSlaRule(ruleId) {
    await apiFetch(`/customers/${currentCustomerId}/sla/${ruleId}`, { method: 'DELETE' });
    setSlaRules(r => r.filter(x => x.id !== ruleId));
  }

  function fillSlaDefaults(priority) {
    const [res, resol] = PRIORITY_DEFAULTS[priority] || [4, 8];
    setSlaForm(f => ({ ...f, priority, response_hours: f.response_hours || res, resolution_hours: f.resolution_hours || resol }));
  }

  function handleCategoryChange(e) {
    // Category is only a filter for the "add product" helper now — it must NOT
    // clear the products field (a client can have several products).
    setForm(f => ({ ...f, product_category: e.target.value }));
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(''); setSaving(true);
    try {
      const { product_category, ...payload } = form;
      if (modal === 'add') {
        await apiFetch('/customers', { method: 'POST', body: payload });
      } else {
        await apiFetch(`/customers/${modal.id}`, { method: 'PATCH', body: payload });
      }
      setModal(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await apiFetch(`/customers/${id}`, { method: 'DELETE' });
      setDeleteConfirm(null);
      load();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  }

  // ── Email domain actions (edit mode only) ──
  async function addDomain() {
    if (!newDomain.trim() || !currentCustomerId) return;
    setDomainErr('');
    try {
      const d = await apiFetch(`/customers/${currentCustomerId}/domains`, { method: 'POST', body: { domain: newDomain.trim() } });
      setDomains(ds => [...ds.filter(x => x.id !== d.id), d].sort((a, b) => a.domain.localeCompare(b.domain)));
      setNewDomain('');
    } catch (e) { setDomainErr(e.message); }
  }
  async function removeDomain(did) {
    if (!window.confirm('Remove this domain?')) return;
    try { await apiFetch(`/customers/${currentCustomerId}/domains/${did}`, { method: 'DELETE' }); setDomains(ds => ds.filter(x => x.id !== did)); }
    catch (e) { setDomainErr(e.message); }
  }

  // ── Location actions (only available when editing an existing customer) ──
  function startAddLoc() {
    setEditingLoc(null);
    setLocForm(EMPTY_LOC);
    setLocError('');
    setShowLocForm(true);
  }

  function startEditLoc(loc) {
    setEditingLoc(loc);
    setLocForm({ location: loc.location, spoc_name: loc.spoc_name || '', spoc_designation: loc.spoc_designation || '', spoc_department: loc.spoc_department || '', spoc_phone: loc.spoc_phone || '', spoc_email: loc.spoc_email || '' });
    setLocError('');
    setShowLocForm(true);
  }

  async function saveLoc(e) {
    e.preventDefault();
    if (!currentCustomerId) return;
    setLocError(''); setLocSaving(true);
    try {
      if (editingLoc) {
        const updated = await apiFetch(`/customers/${currentCustomerId}/locations/${editingLoc.id}`, { method: 'PATCH', body: locForm });
        setLocations(ls => ls.map(l => l.id === editingLoc.id ? updated : l));
      } else {
        const added = await apiFetch(`/customers/${currentCustomerId}/locations`, { method: 'POST', body: locForm });
        setLocations(ls => [...ls, added]);
      }
      setShowLocForm(false);
      setLocForm(EMPTY_LOC);
      setEditingLoc(null);
    } catch (err) {
      setLocError(err.message);
    } finally {
      setLocSaving(false);
    }
  }

  async function deleteLoc(locId) {
    await apiFetch(`/customers/${currentCustomerId}/locations/${locId}`, { method: 'DELETE' });
    setLocations(ls => ls.filter(l => l.id !== locId));
  }

  async function handleImport(e) {
    const file = e.target.files[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const token = sessionStorage.getItem('kgreen_token');
      const res = await fetch('/api/customers/import', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Import failed');
      setImportResult({ ...json, ok: true });
      if (json.inserted > 0) load();
    } catch (err) {
      setImportResult({ ok: false, error: err.message });
    } finally {
      setImporting(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      // Fetch all customers (bypass pagination)
      const params = search ? `?search=${encodeURIComponent(search)}&limit=5000` : '?limit=5000';
      const d = await apiFetch(`/customers${params}`);
      exportToExcel(d.customers || [], [
        { header: 'Customer Name',  key: 'name' },
        { header: 'Address',        key: 'address' },
        { header: 'City',           key: 'city' },
        { header: 'State',          key: 'state' },
        { header: 'SPOC Name',      key: 'spoc_name' },
        { header: 'Contact Number', key: 'contact_number' },
        { header: 'Email',          key: 'customer_email' },
        { header: 'Product',        key: 'product' },
        { header: 'Serial Number',  key: 'serial_number' },
        { header: 'AMC Start Date', key: 'amc_start_date' },
        { header: 'AMC End Date',   key: 'amc_end_date' },
        { header: 'PO Number',      key: 'po_number' },
        { header: 'PO Date',        key: 'po_date' },
        { header: 'Notes',          key: 'notes' },
        { header: 'Created At',     key: 'created_at' },
      ], `customers-export-${new Date().toISOString().slice(0,10)}.xlsx`);
    } finally {
      setExporting(false);
    }
  }

  // Compact field helper — less bottom margin
  const f = (k, label, type = 'text', required = false) => (
    <div style={{ marginBottom: 8 }}>
      <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>{label}{required && ' *'}</label>
      <input className="input" style={{ padding: '7px 10px', fontSize: 13 }} type={type} value={form[k]}
        onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))} required={required} />
    </div>
  );

  const lf = (k, label, type = 'text') => (
    <div className="form-group" style={{ marginBottom: 10 }}>
      <label className="label" style={{ fontSize: 12 }}>{label}</label>
      <input className="input" style={{ fontSize: 13 }} type={type} value={locForm[k]}
        onChange={e => setLocForm(f => ({ ...f, [k]: e.target.value }))} />
    </div>
  );

  const sec = { background: 'var(--bg-app)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 };
  const secTitle = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 8 };

  return (
    <div style={ isMobile
        ? { width: '100%', boxSizing: 'border-box', overflowX: 'hidden', padding: '14px 10px 40px' }
        : { maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? 10 : 0, marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>Customers</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginTop: 2 }}>{data.total} total</p>
        </div>
        <div className="kg-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <a
            href="/kgreen-customer-import-template.xlsx"
            download="kgreen-customer-import-template.xlsx"
            className="btn btn-secondary"
            style={{ fontSize: 13 }}
          >⬇ Template</a>
          <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => fileInputRef.current?.click()} disabled={importing}>
            {importing ? 'Importing…' : '📥 Import Excel'}
          </button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} onChange={handleImport} />
          <button className="btn btn-secondary" style={{ fontSize: 13 }} onClick={handleExport} disabled={exporting || data.total === 0}>
            {exporting ? '⏳ Exporting…' : '📊 Export to Excel'}
          </button>
          <button className="btn btn-primary" onClick={openAdd}>+ Add Customer</button>
        </div>
      </div>

      <div style={{ marginBottom: 20 }}>
        <input ref={searchRef} className="input" style={{ maxWidth: 320 }} placeholder="Search by name, product, serial…"
          value={search} onChange={e => { setSearch(e.target.value); setSelectedIdx(-1); }} />
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><span className="spinner" /></div>
      ) : visibleCustomers.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏢</div>
          <p>No customers yet.</p>
          <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={openAdd}>Add First Customer</button>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {data.total > PAGE_SIZE && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, padding: '8px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
              {Array.from({ length: Math.ceil(data.total / PAGE_SIZE) }, (_, i) => i + 1).map(p => (
                <button key={p} className="btn btn-sm"
                  style={{ minWidth: 30, background: p === page ? 'var(--status-resolved-bg)' : 'transparent', color: p === page ? 'var(--status-resolved-text)' : 'var(--text-secondary)', border: `1px solid ${p === page ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}`, fontWeight: p === page ? 700 : 400 }}
                  onClick={() => setPage(p)}>{p}</button>
              ))}
            </div>
          )}
          {isMobile ? (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {visibleCustomers.map(c => {
                const status = amcStatus(c.amc_end_date);
                return (
                  <div key={c.id} onClick={() => openEdit(c)}
                    style={{ padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontWeight: 600, fontSize: 14, minWidth: 0 }}>{c.name}</div>
                      {status && <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: status.bg, color: status.color, whiteSpace: 'nowrap', flexShrink: 0 }}>{status.label}</span>}
                    </div>
                    {c.product && <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>📦 {c.product}</div>}
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                      {c.city && <span>📍 {c.city}</span>}
                      {c.spoc_name && <span>👤 {c.spoc_name}</span>}
                      {c.contact_number && <span>📞 {c.contact_number}</span>}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }} onClick={e => e.stopPropagation()}>
                      <button className="btn btn-secondary btn-sm" onClick={() => openEdit(c)}>Edit</button>
                      <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }} onClick={() => setDeleteConfirm(c)}>Delete</button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                {['Name / Product', 'City', 'SPOC', 'Contact', 'Serial No.', 'AMC End', 'Status', ''].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visibleCustomers.map((c, idx) => {
                const status = amcStatus(c.amc_end_date);
                const isSelected = idx === selectedIdx;
                return (
                  <React.Fragment key={c.id}><tr onClick={() => setSelectedIdx(idx)} style={{ borderBottom: c.product ? 'none' : '1px solid var(--border-subtle)', ...(isSelected ? { background: 'var(--action-bg)', outline: '2px solid #0B6623', outlineOffset: -2 } : {}) }}>
                    <td style={{ padding: '12px 14px 4px', fontWeight: 500 }}>{c.name}</td>
                    <td style={{ padding: '12px 14px 4px', color: 'var(--text-secondary)' }}>{c.city || '—'}</td>
                    <td style={{ padding: '12px 14px 4px', color: 'var(--text-muted)' }}>{c.spoc_name || '—'}</td>
                    <td style={{ padding: '12px 14px 4px', color: 'var(--text-secondary)', fontSize: 13 }}>
                      {c.contact_number && <div>{c.contact_number}</div>}
                      {c.customer_email && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.customer_email}</div>}
                      {!c.contact_number && !c.customer_email && '—'}
                    </td>
                    <td style={{ padding: '12px 14px', fontFamily: 'monospace', fontSize: 13 }}>{c.serial_number || '—'}</td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{c.amc_end_date || '—'}</td>
                    <td style={{ padding: '12px 14px' }}>
                      {status ? <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 500, background: status.bg, color: status.color }}>{status.label}</span> : '—'}
                    </td>
                    <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-secondary btn-sm" style={{ marginRight: 6 }} onClick={() => openEdit(c)}>Edit</button>
                      {isAdmin && (
                        <button
                          className="btn btn-sm"
                          title={c.mfa_enabled ? 'MFA enabled — click to disable' : 'MFA disabled — click to enable'}
                          style={{ marginRight: 6, background: c.mfa_enabled ? 'var(--status-resolved-bg)' : 'var(--bg-app)', color: c.mfa_enabled ? 'var(--status-resolved-text)' : 'var(--text-muted)', border: `1px solid ${c.mfa_enabled ? 'var(--status-resolved-text)' : 'var(--border-subtle)'}` }}
                          onClick={async () => {
                            try {
                              const json = await apiFetch(`/auth/customers/${c.id}/mfa-toggle`, { method: 'PATCH' });
                              setData(prev => ({
                                ...prev,
                                customers: prev.customers.map(x => x.id === c.id ? { ...x, mfa_enabled: json.mfa_enabled } : x),
                              }));
                            } catch (err) { alert('Failed to toggle MFA: ' + err.message); }
                          }}
                        >
                          {c.mfa_enabled ? '🔐 MFA On' : '🔓 MFA Off'}
                        </button>
                      )}
                      <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }} onClick={() => setDeleteConfirm(c)}>Delete</button>
                    </td>
                  </tr>
                  {c.product && (
                    <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td colSpan={4} style={{ padding: '0 14px 8px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        📦 {c.product}
                      </td>
                      <td colSpan={4} />
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          )}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'center', zIndex: 999, padding: isMobile ? 0 : 24 }}>
          <div className="card" style={ isMobile
              ? { width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh', borderRadius: 0, padding: 16, overflowY: 'auto' }
              : { width: modal !== 'add' && (activeTab === 'products' || activeTab === 'sla' || activeTab === 'locations') ? '95vw' : '100%',
                  maxWidth: modal !== 'add' && (activeTab === 'products' || activeTab === 'sla' || activeTab === 'locations') ? 1100 : 680,
                  padding: 32, maxHeight: '92vh', overflowY: 'auto', transition: 'max-width .15s' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700 }}>{modal === 'add' ? 'Add Customer' : `Edit — ${modal.name}`}</h2>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--text-secondary)', cursor: 'pointer' }}>×</button>
            </div>

            {/* Tabs — only show for existing customers */}
            {modal !== 'add' && (
              <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid var(--border-subtle)', marginBottom: 20, overflowX: 'auto', whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                {[['details','📋 Details'], ['products','📦 Products & AMC'], ['locations','📍 Locations'], ['domains','🌐 Domains'], ['users','👥 Users'], ['sla','⏱ SLA']].map(([tab, label]) => (
                  <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                    style={{ padding: '8px 18px', fontSize: 13, fontWeight: activeTab === tab ? 700 : 500,
                      color: activeTab === tab ? 'var(--status-resolved-text)' : 'var(--text-secondary)', background: 'none', border: 'none',
                      borderBottom: activeTab === tab ? '2px solid var(--status-resolved-text)' : '2px solid transparent',
                      marginBottom: -2, cursor: 'pointer' }}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            {error && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}

            {/* ── Details & Locations tabs ── */}
            {(modal === 'add' || activeTab === 'details') && <form onSubmit={handleSave}>
              {/* Row 1: Basic Info */}
              <div style={sec}>
                <p style={secTitle}>Basic Info</p>
                {f('name', 'Customer Name *', 'text', true)}
                {f('address', 'Address')}
                <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 10 }}>
                  {f('city', 'City')}
                  <div style={{ marginBottom: 8 }}>
                    <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>State</label>
                    <select className="input" style={{ padding: '7px 10px', fontSize: 13 }} value={form.state}
                      onChange={e => setForm(f => ({ ...f, state: e.target.value }))}>
                      <option value="">— Select state —</option>
                      {INDIA_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Row 2: SPOC */}
              <div style={sec}>
                <p style={secTitle}>Customer SPOC</p>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  {f('spoc_name', 'SPOC Name')}
                  {f('contact_number', 'Contact Number', 'tel')}
                  <div style={{ marginBottom: 8 }}>
                    <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Email ID</label>
                    <input className="input" style={{ padding: '7px 10px', fontSize: 13 }} type="email" value={form.customer_email}
                      onChange={e => {
                        const email = e.target.value;
                        const domain = email.includes('@') ? email.split('@')[1].trim() : '';
                        setForm(f => ({ ...f, customer_email: email, ...(domain ? { email_domain: domain } : {}) }));
                      }} />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10, marginTop: 4 }}>
                  <div style={{ marginBottom: 8 }}>
                    <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>
                      Email Domain
                      {form.email_domain && !isAdmin && (
                        <span style={{ marginLeft: 8, fontSize: 11, color: '#b91c1c', fontWeight: 600 }}>🔒 Locked — contact super admin to change</span>
                      )}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)', fontSize: 13 }}>@</span>
                      <input className="input" style={{ padding: '7px 10px 7px 22px', fontSize: 13, background: form.email_domain && !isAdmin ? 'var(--bg-app)' : 'var(--bg-surface)', color: form.email_domain && !isAdmin ? 'var(--text-secondary)' : undefined }}
                        placeholder="cosmos.com"
                        value={form.email_domain}
                        readOnly={!!(form.email_domain && !isAdmin)}
                        onChange={e => { if (isAdmin || !form.email_domain) setForm(f => ({ ...f, email_domain: e.target.value.replace(/^@/, '') })); }} />
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>Emails from this domain auto-raise tickets</p>
                  </div>
                </div>
              </div>

              {/* Row 3: Product — only when adding a new customer. In edit mode,
                  products (and their AMC/PO/serial) are managed in the Products & AMC tab. */}
              {modal === 'add' && (
              <div style={sec}>
                <p style={secTitle}>Products</p>
                {/* Products stored as free text; a client can have several (separated by " | ") */}
                <div style={{ marginBottom: 8 }}>
                  <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Products (one or more)</label>
                  <textarea className="input" rows={2} style={{ padding: '7px 10px', fontSize: 13, resize: 'vertical' }}
                    value={form.product}
                    onChange={e => setForm(f => ({ ...f, product: e.target.value }))}
                    placeholder='e.g. Sectona PAM | Active Directory' />
                  {form.product && form.product.includes('|') && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 6 }}>
                      {form.product.split('|').map(s => s.trim()).filter(Boolean).map((p, i) => (
                        <span key={i} style={{ fontSize: 11, fontWeight: 600, background: 'var(--action-bg)', color: '#0B6623',
                                               border: '1px solid #bbf7d0', borderRadius: 99, padding: '2px 9px' }}>{p}</span>
                      ))}
                    </div>
                  )}
                </div>
                {/* Quick-add from catalog — appends, never overwrites */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={{ marginBottom: 8 }}>
                    <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Category</label>
                    <select className="input" style={{ padding: '7px 10px', fontSize: 13 }} value={form.product_category} onChange={handleCategoryChange}>
                      <option value="">— Select category —</option>
                      {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                    </select>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Add product from catalog</label>
                    <select className="input" style={{ padding: '7px 10px', fontSize: 13 }} value=""
                      onChange={e => {
                        const name = e.target.value;
                        if (!name) return;
                        setForm(f => {
                          const cur = (f.product || '').split('|').map(s => s.trim()).filter(Boolean);
                          if (cur.includes(name)) return f;
                          return { ...f, product: [...cur, name].join(' | ') };
                        });
                      }}
                      disabled={!form.product_category}>
                      <option value="">{form.product_category ? '— Add product —' : '← Category first'}</option>
                      {filteredProducts.map(p => (
                        <option key={p.id} value={p.name}>
                          {p.name}{p.manufacturer ? ` — ${p.manufacturer}` : ''}{p.model ? ` ${p.model}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {f('serial_number', 'Serial Number')}
              </div>
              )}

              {/* Rows 4 & 5: PO + AMC — add mode only. In edit mode these are per-product
                  in the Products & AMC tab. */}
              {modal === 'add' && (
              <>
                <div style={sec}>
                  <p style={secTitle}>Purchase Order</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {f('po_number', 'PO Number')}
                    {f('po_date', 'PO Date', 'date')}
                  </div>
                </div>
                <div style={sec}>
                  <p style={secTitle}>AMC</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    {f('amc_start_date', 'AMC Start Date', 'date')}
                    {f('amc_end_date', 'AMC End Date', 'date')}
                  </div>
                </div>
              </>
              )}

              {/* Notes */}
              <div style={{ marginBottom: 12 }}>
                <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Notes</label>
                <textarea className="input" style={{ padding: '7px 10px', fontSize: 13 }} rows={2} value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ resize: 'vertical', padding: '7px 10px', fontSize: 13 }} />
              </div>

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModal(null)}>Cancel</button>
                <button className="btn btn-primary btn-sm" disabled={saving}>
                  {saving ? 'Saving…' : (modal === 'add' ? 'Add Customer' : 'Save Changes')}
                </button>
              </div>
            </form>}

            {/* ── Products & AMC tab ── */}
            {modal !== 'add' && activeTab === 'products' && (
              <div>
                <div style={{ marginBottom: 14 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Products & AMC</h3>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>Each product has its own AMC start / end date and serial number.</p>
                </div>

                {prodRows.length === 0 ? (
                  <div style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '12px 0' }}>No products yet. Add one below.</div>
                ) : isMobile ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                    {prodRows.map(p => {
                      const di = { padding: '6px 8px', borderRadius: 6, border: '1.5px solid var(--n-300)', fontSize: 13, background: 'var(--bg-surface)', color: 'var(--text-primary)', boxSizing: 'border-box', width: '100%' };
                      const lbl = { fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3, textTransform: 'uppercase' };
                      const inactive = p.status === 'inactive';
                      const expired = p.amc_end_date && new Date(p.amc_end_date) < new Date();
                      return (
                        <div key={p.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, padding: 12, opacity: inactive ? 0.7 : 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, textDecoration: inactive ? 'line-through' : 'none' }}>{p.product_name}</div>
                            <button type="button" onClick={() => removeProduct(p.id)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18, lineHeight: 1 }}>×</button>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                            <div>
                              <label style={lbl}>Status</label>
                              <select style={{ ...di, fontWeight: 700, color: inactive ? '#b45309' : '#065f46' }} value={p.status || 'active'}
                                onChange={e => { updateProductField(p.id, 'status', e.target.value); saveProductField(p.id, 'status', e.target.value); }}>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            </div>
                            <div>
                              <label style={lbl}>PO Number</label>
                              <input type="text" style={di} value={p.po_number || ''} placeholder="—"
                                onChange={e => updateProductField(p.id, 'po_number', e.target.value)}
                                onBlur={e => saveProductField(p.id, 'po_number', e.target.value)} />
                            </div>
                            <div>
                              <label style={lbl}>AMC Start</label>
                              <input type="date" style={di} value={p.amc_start_date || ''}
                                onChange={e => updateProductField(p.id, 'amc_start_date', e.target.value)}
                                onBlur={e => saveProductField(p.id, 'amc_start_date', e.target.value)} />
                            </div>
                            <div>
                              <label style={lbl}>AMC End {expired && !inactive && <span style={{ color: '#dc2626' }}>· expired</span>}</label>
                              <input type="date" style={{ ...di, borderColor: expired && !inactive ? '#fca5a5' : 'var(--n-300)' }} value={p.amc_end_date || ''}
                                onChange={e => updateProductField(p.id, 'amc_end_date', e.target.value)}
                                onBlur={e => saveProductField(p.id, 'amc_end_date', e.target.value)} />
                            </div>
                            <div style={{ gridColumn: '1/-1' }}>
                              <label style={lbl}>Serial No.</label>
                              <input type="text" style={di} value={p.serial_number || ''} placeholder="—"
                                onChange={e => updateProductField(p.id, 'serial_number', e.target.value)}
                                onBlur={e => saveProductField(p.id, 'serial_number', e.target.value)} />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 10, marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 820 }}>
                      <thead>
                        <tr style={{ background: 'var(--n-50)' }}>
                          {['Product','Status','AMC Start','AMC End','PO Number','Serial No.',''].map(h => (
                            <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .4 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {prodRows.map(p => {
                          const di = { width: '100%', padding: '5px 7px', borderRadius: 6, border: '1.5px solid var(--n-300)', fontSize: 12.5, background: 'var(--bg-surface)', color: 'var(--text-primary)', boxSizing: 'border-box' };
                          const expired = p.amc_end_date && new Date(p.amc_end_date) < new Date();
                          const inactive = p.status === 'inactive';
                          return (
                            <tr key={p.id} style={{ borderTop: '1px solid var(--border-subtle)', opacity: inactive ? 0.6 : 1 }}>
                              <td style={{ padding: '8px 12px', fontWeight: 600, textDecoration: inactive ? 'line-through' : 'none' }}>{p.product_name}</td>
                              <td style={{ padding: '8px 12px' }}>
                                <select style={{ ...di, fontWeight: 700, color: inactive ? '#b45309' : '#065f46' }} value={p.status || 'active'}
                                  onChange={e => { updateProductField(p.id, 'status', e.target.value); saveProductField(p.id, 'status', e.target.value); }}>
                                  <option value="active">Active</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input type="date" style={di} value={p.amc_start_date || ''}
                                  onChange={e => updateProductField(p.id, 'amc_start_date', e.target.value)}
                                  onBlur={e => saveProductField(p.id, 'amc_start_date', e.target.value)} />
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input type="date" style={{ ...di, borderColor: expired && !inactive ? '#fca5a5' : 'var(--n-300)' }} value={p.amc_end_date || ''}
                                  onChange={e => updateProductField(p.id, 'amc_end_date', e.target.value)}
                                  onBlur={e => saveProductField(p.id, 'amc_end_date', e.target.value)} />
                                {expired && !inactive && <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2 }}>expired</div>}
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input type="text" style={di} value={p.po_number || ''} placeholder="—"
                                  onChange={e => updateProductField(p.id, 'po_number', e.target.value)}
                                  onBlur={e => saveProductField(p.id, 'po_number', e.target.value)} />
                              </td>
                              <td style={{ padding: '8px 12px' }}>
                                <input type="text" style={di} value={p.serial_number || ''} placeholder="—"
                                  onChange={e => updateProductField(p.id, 'serial_number', e.target.value)}
                                  onBlur={e => saveProductField(p.id, 'serial_number', e.target.value)} />
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                <button type="button" onClick={() => removeProduct(p.id)} title="Remove product"
                                  style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Add product */}
                <div style={{ background: 'var(--action-bg)', border: '1px solid #bbf7d0', borderRadius: 10, padding: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#065f46', marginBottom: 8 }}>Add a product</div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1.4fr 0.8fr 1fr 1fr 1fr 1fr auto', gap: 8, alignItems: 'end' }}>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Product</label>
                      <input list="cust-prod-catalog" className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} placeholder="Type or pick…"
                        value={prodForm.product_name} onChange={e => setProdForm(f => ({ ...f, product_name: e.target.value }))} />
                      <datalist id="cust-prod-catalog">{allProducts.map(p => <option key={p.id} value={p.name} />)}</datalist>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Status</label>
                      <select className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} value={prodForm.status} onChange={e => setProdForm(f => ({ ...f, status: e.target.value }))}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>AMC Start</label>
                      <input type="date" className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} value={prodForm.amc_start_date} onChange={e => setProdForm(f => ({ ...f, amc_start_date: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>AMC End</label>
                      <input type="date" className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} value={prodForm.amc_end_date} onChange={e => setProdForm(f => ({ ...f, amc_end_date: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>PO Number</label>
                      <input type="text" className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} value={prodForm.po_number} onChange={e => setProdForm(f => ({ ...f, po_number: e.target.value }))} />
                    </div>
                    <div>
                      <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 3 }}>Serial</label>
                      <input type="text" className="input" style={{ fontSize: 12.5, padding: '6px 9px' }} value={prodForm.serial_number} onChange={e => setProdForm(f => ({ ...f, serial_number: e.target.value }))} />
                    </div>
                    <button type="button" className="btn btn-primary btn-sm" onClick={addProduct} disabled={prodSaving || !prodForm.product_name.trim()} style={{ height: 34, gridColumn: isMobile ? '1 / -1' : 'auto' }}>Add</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Locations tab ── */}
            {modal !== 'add' && activeTab === 'locations' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Locations & SPOC</h3>
                  {!showLocForm && (
                    <button className="btn btn-secondary btn-sm" onClick={startAddLoc}>+ Add Location</button>
                  )}
                </div>

                {/* Location form */}
                {showLocForm && (
                  <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-resolved-text)', marginBottom: 12 }}>
                      {editingLoc ? 'Edit Location' : 'New Location'}
                    </p>
                    {locError && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{locError}</div>}
                    <form onSubmit={saveLoc}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div style={{ gridColumn: '1 / -1' }}>
                          <div className="form-group" style={{ marginBottom: 10 }}>
                            <label className="label" style={{ fontSize: 12 }}>Location *</label>
                            <input className="input" style={{ fontSize: 13 }} value={locForm.location}
                              onChange={e => setLocForm(f => ({ ...f, location: e.target.value }))} required autoFocus />
                          </div>
                        </div>
                        {lf('spoc_name', 'SPOC Name')}
                        {lf('spoc_designation', 'Designation')}
                        {lf('spoc_department', 'Department')}
                        {lf('spoc_phone', 'Phone Number', 'tel')}
                        <div style={{ gridColumn: '1 / -1' }}>
                          {lf('spoc_email', 'Email', 'email')}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowLocForm(false); setEditingLoc(null); }}>Cancel</button>
                        <button className="btn btn-primary btn-sm" disabled={locSaving}>
                          {locSaving ? 'Saving…' : (editingLoc ? 'Update' : 'Add Location')}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Locations list */}
                {locations.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '12px 0' }}>No locations added yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {locations.map(loc => (
                      <div key={loc.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '14px 16px', background: 'var(--bg-surface)' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                          <div>
                            <p style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>📍 {loc.location}</p>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13, color: 'var(--text-secondary)' }}>
                              {loc.spoc_name && <span>👤 {loc.spoc_name}</span>}
                              {loc.spoc_designation && <span>🏷 {loc.spoc_designation}</span>}
                              {loc.spoc_department && <span>🏢 {loc.spoc_department}</span>}
                              {loc.spoc_phone && <span>📞 {loc.spoc_phone}</span>}
                              {loc.spoc_email && <span>✉ {loc.spoc_email}</span>}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => startEditLoc(loc)}>Edit</button>
                            <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }} onClick={() => deleteLoc(loc.id)}>Delete</button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Domains tab ── */}
            {modal !== 'add' && activeTab === 'domains' && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 14, lineHeight: 1.6 }}>
                  Emails from these domains auto-create tickets for this customer and register the sender as a customer user. Add every domain this customer emails from.
                </p>
                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input className="input" placeholder="acme.com" value={newDomain}
                    onChange={e => setNewDomain(e.target.value.replace(/^@/, ''))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }} style={{ flex: 1 }} />
                  <button type="button" className="btn btn-primary" onClick={addDomain} disabled={!newDomain.trim()} style={{ background: '#0B6623' }}>+ Add domain</button>
                </div>
                {domainErr && <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>⚠ {domainErr}</div>}
                {domains.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic', marginTop: 10 }}>No domains yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                    {domains.map(d => (
                      <div key={d.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '9px 12px' }}>
                        <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace' }}>@{d.domain}</span>
                        <button type="button" className="btn btn-sm" style={{ background: 'var(--red-50)', color: '#b91c1c' }} onClick={() => removeDomain(d.id)}>Remove</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── Users tab ── */}
            {modal !== 'add' && activeTab === 'users' && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 600 }}>Portal Users</h3>
                  {!showUserForm && (
                    <button className="btn btn-secondary btn-sm" onClick={() => { setShowUserForm(true); setUserError(''); }}>+ Add User</button>
                  )}
                </div>

                {showUserForm && (
                  <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-resolved-text)', marginBottom: 12 }}>New Portal User</p>
                    {userError && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{userError}</div>}
                    <form onSubmit={saveUser}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Full Name *</label>
                          <input className="input" style={{ fontSize: 13 }} value={userForm.name}
                            onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} required autoFocus />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Email *</label>
                          <input className="input" type="email" style={{ fontSize: 13 }} value={userForm.email}
                            onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} required />
                        </div>
                        <div>
                          <label className="label" style={{ fontSize: 12 }}>Role</label>
                          <select className="input" style={{ fontSize: 13 }} value={userForm.role}
                            onChange={e => setUserForm(f => ({ ...f, role: e.target.value }))}>
                            <option value="customer">Customer</option>
                            <option value="customer_manager">Customer Manager</option>
                          </select>
                        </div>
                      </div>
                      <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 10 }}>A temporary password will be auto-assigned. Ask the user to reset it on first login.</p>
                      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowUserForm(false)}>Cancel</button>
                        <button className="btn btn-primary btn-sm" disabled={userSaving}>{userSaving ? 'Adding…' : 'Add User'}</button>
                      </div>
                    </form>
                  </div>
                )}

                {custUsers.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>No portal users linked to this customer yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {custUsers.map(u => (
                      <div key={u.id} style={{ border: '1px solid var(--border-subtle)', borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-surface)' }}>
                        <div>
                          <p style={{ fontWeight: 600, fontSize: 14, margin: 0 }}>{u.name}</p>
                          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '3px 0 0' }}>{u.email}</p>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 999,
                            background: u.role === 'customer_manager' ? 'var(--amber-100)' : 'var(--action-bg)',
                            color: u.role === 'customer_manager' ? '#92400e' : '#094d1e' }}>
                            {u.role === 'customer_manager' ? 'Manager' : 'Customer'}
                          </span>
                          <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }} onClick={() => deleteUser(u.id)}>Remove</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {modal === 'add' && (
              <p style={{ marginTop: 16, fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center' }}>
                You can add locations, SPOC contacts and SLA rules after saving the customer.
              </p>
            )}

            {/* ── SLA tab ── */}
            {modal !== 'add' && activeTab === 'sla' && (
              <div>
                <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                  Set custom SLA response & resolution hours for this customer, optionally per product.
                  Leave product blank to apply to <strong>all products</strong> for this customer.
                  Custom rules override the global SLA policy.
                </p>

                {/* Add rule form */}
                <form onSubmit={saveSlaRule} style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--status-resolved-text)', marginBottom: 12 }}>Add / Update SLA Rule</p>
                  {slaError && <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: 6, marginBottom: 10, fontSize: 13 }}>{slaError}</div>}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Product (optional)</label>
                      <select className="input" style={{ fontSize: 13 }} value={slaForm.product_id}
                        onChange={e => setSlaForm(f => ({ ...f, product_id: e.target.value }))}>
                        <option value="">— All products —</option>
                        {allProducts.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Priority</label>
                      <select className="input" style={{ fontSize: 13 }} value={slaForm.priority}
                        onChange={e => fillSlaDefaults(e.target.value)}>
                        {PRIORITIES_SLA.map(p => <option key={p} value={p} style={{ textTransform: 'capitalize' }}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Response Time (hours)</label>
                      <input className="input" type="number" min="0.5" step="0.5" style={{ fontSize: 13 }}
                        placeholder={`e.g. ${PRIORITY_DEFAULTS[slaForm.priority]?.[0] ?? 4}`}
                        value={slaForm.response_hours}
                        onChange={e => setSlaForm(f => ({ ...f, response_hours: e.target.value }))} required />
                    </div>
                    <div>
                      <label className="label" style={{ fontSize: 12, marginBottom: 3 }}>Resolution Time (hours)</label>
                      <input className="input" type="number" min="0.5" step="0.5" style={{ fontSize: 13 }}
                        placeholder={`e.g. ${PRIORITY_DEFAULTS[slaForm.priority]?.[1] ?? 8}`}
                        value={slaForm.resolution_hours}
                        onChange={e => setSlaForm(f => ({ ...f, resolution_hours: e.target.value }))} required />
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button className="btn btn-primary btn-sm" disabled={slaSaving}>
                      {slaSaving ? 'Saving…' : '+ Save Rule'}
                    </button>
                  </div>
                </form>

                {/* Existing rules */}
                {slaRules.length === 0 ? (
                  <p style={{ fontSize: 13, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
                    No custom SLA rules. Global policy applies.
                  </p>
                ) : (
                  <div>
                    <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>Active Rules</p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-app)', borderBottom: '1px solid var(--border-subtle)' }}>
                          {['Product', 'Priority', 'Response (hrs)', 'Resolution (hrs)', ''].map(h => (
                            <th key={h} style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--text-secondary)', fontWeight: 500, fontSize: 12 }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {slaRules.map(r => (
                          <tr key={r.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                            <td style={{ padding: '9px 12px', fontWeight: 500 }}>{r.product_name || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>All products</span>}</td>
                            <td style={{ padding: '9px 12px' }}>
                              <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                                background: { critical:'var(--red-50)', high:'var(--amber-100)', medium:'var(--blue-100)', low:'var(--n-100)' }[r.priority],
                                color: { critical:'#b91c1c', high:'#92400e', medium:'var(--blue-700)', low:'var(--text-muted)' }[r.priority] }}>
                                {r.priority}
                              </span>
                            </td>
                            <td style={{ padding: '9px 12px' }}>{r.response_hours}h</td>
                            <td style={{ padding: '9px 12px' }}>{r.resolution_hours}h</td>
                            <td style={{ padding: '9px 12px' }}>
                              <button className="btn btn-sm" style={{ background: '#fee2e2', color: '#b91c1c' }}
                                onClick={() => deleteSlaRule(r.id)}>Remove</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Import result modal */}
      {importResult && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 24 }}>
          <div className="card" style={{ width: '100%', maxWidth: 480, padding: 32 }}>
            {importResult.ok ? (
              <>
                <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>✅</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 20 }}>Import Complete</h2>
                <div style={{ background: 'var(--action-bg)', border: '1px solid var(--action-hover)', borderRadius: 10, padding: '16px 20px', marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Customers added</span>
                    <span style={{ fontWeight: 700, color: 'var(--status-resolved-text)' }}>{importResult.inserted}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, marginBottom: 8 }}>
                    <span style={{ color: 'var(--text-muted)' }}>Locations added</span>
                    <span style={{ fontWeight: 700, color: 'var(--status-resolved-text)' }}>{importResult.locationsInserted}</span>
                  </div>
                  {importResult.skipped > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
                      <span style={{ color: 'var(--text-muted)' }}>Skipped (already exist)</span>
                      <span style={{ fontWeight: 700, color: '#92400e' }}>{importResult.skipped}</span>
                    </div>
                  )}
                </div>
                {importResult.errors && importResult.errors.length > 0 && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#b91c1c', marginBottom: 8 }}>⚠ Errors ({importResult.errors.length})</p>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#7f1d1d', maxHeight: 150, overflowY: 'auto' }}>
                      {importResult.errors.map((e, i) => <li key={i}><strong>{e.row}</strong>: {e.error}</li>)}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 12 }}>❌</div>
                <h2 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 16 }}>Import Failed</h2>
                <p style={{ color: '#b91c1c', textAlign: 'center', fontSize: 14, marginBottom: 20 }}>{importResult.error}</p>
              </>
            )}
            <div style={{ textAlign: 'center' }}>
              <button className="btn btn-primary" onClick={() => setImportResult(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div className="card" style={{ padding: 32, maxWidth: 400, width: '100%' }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Delete Customer?</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 24 }}>Permanently deletes <strong>{deleteConfirm.name}</strong> and all their locations.</p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => handleDelete(deleteConfirm.id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
